import {
  BOARD_HEIGHT,
  BOARD_WIDTH,
  type Board,
  type Cell,
  type CellGravityMove,
  type FallingPair,
  type MarkGrid,
  type PairOrientation,
  cloneBoard,
  createEmptyBoard,
  createMarkGrid,
  inBounds,
  pairCells,
  pairWithPosition
} from "./model";
import { activeTypeCount, applyErasedToLevel, scoreForWave } from "./scoring";

export interface ResolveWave {
  erased: number;
  chain: number;
  scoreAdd: number;
  levelBefore: number;
  levelAfter: number;
  levelUps: number;
}

export interface ResolveResult {
  board: Board;
  waves: ResolveWave[];
  scoreAdd: number;
  level: number;
  levelProgress: number;
  erased: number;
}

export function canPlacePair(board: Board, pair: FallingPair): boolean {
  return pairCells(pair).every((cell) => {
    if (cell.x < 0 || cell.x >= BOARD_WIDTH || cell.y >= BOARD_HEIGHT) {
      return false;
    }
    if (cell.y < 0) {
      return true;
    }
    return board[cell.y][cell.x] === 0;
  });
}

export function movePair(board: Board, pair: FallingPair, dx: number, dy: number): FallingPair | null {
  const next = pairWithPosition(pair, pair.x + dx, pair.y + dy);
  return canPlacePair(board, next) ? next : null;
}

export function swapPair(pair: FallingPair): FallingPair {
  return {
    ...pair,
    upperType: pair.lowerType,
    lowerType: pair.upperType
  };
}

export function rotatePair(board: Board, pair: FallingPair, direction: 1 | -1 = 1): FallingPair | null {
  const pivot = pairCells(pair)[1];
  const orientation = (((pair.orientation ?? 0) + direction + 4) % 4) as PairOrientation;
  const rotatedAtSameAnchor = { ...pair, orientation };
  const rotatedLower = pairCells(rotatedAtSameAnchor)[1];
  const rotated = pairWithPosition(rotatedAtSameAnchor, pair.x + pivot.x - rotatedLower.x, pair.y + pivot.y - rotatedLower.y);
  for (const [dx, dy] of [
    [0, 0],
    [-1, 0],
    [1, 0],
    [0, -1],
    [0, 1],
    [-2, 0],
    [2, 0]
  ]) {
    const kicked = pairWithPosition(rotated, rotated.x + dx, rotated.y + dy);
    if (canPlacePair(board, kicked)) {
      return kicked;
    }
  }
  return null;
}

export function landingPair(board: Board, pair: FallingPair): FallingPair {
  let ghost = { ...pair };
  while (canPlacePair(board, { ...ghost, y: ghost.y + 1 })) {
    ghost = { ...ghost, y: ghost.y + 1 };
  }
  return ghost;
}

export function lockPair(board: Board, pair: FallingPair): Board {
  const next = cloneBoard(board);
  for (const cell of pairCells(pair)) {
    if (inBounds(cell.x, cell.y)) {
      next[cell.y][cell.x] = cell.type;
    }
  }
  return next;
}

export function findMatches(board: Board, typeCount = activeTypeCount(1)): MarkGrid {
  const marked = createMarkGrid();
  let count = 0;

  for (let y = 0; y <= BOARD_HEIGHT - 3; y += 1) {
    for (let x = 0; x <= BOARD_WIDTH - 3; x += 1) {
      for (let type = 1; type <= typeCount; type += 1) {
        const cells: Array<[number, number]> = [];
        for (let dy = 0; dy < 3; dy += 1) {
          for (let dx = 0; dx < 3; dx += 1) {
            if (board[y + dy][x + dx] === type) {
              cells.push([x + dx, y + dy]);
            }
          }
        }

        if (cells.length >= 5) {
          for (const [cellX, cellY] of cells) {
            if (!marked[cellY][cellX]) {
              marked[cellY][cellX] = true;
              count += 1;
            }
          }
        }
      }
    }
  }

  return { cells: marked, count };
}

export function eraseMarked(board: Board, marked: boolean[][]): Board {
  const next = cloneBoard(board);
  for (let y = 0; y < BOARD_HEIGHT; y += 1) {
    for (let x = 0; x < BOARD_WIDTH; x += 1) {
      if (marked[y][x]) {
        next[y][x] = 0;
      }
    }
  }
  return next;
}

export function applyColumnGravity(board: Board): Board {
  return applyColumnGravityWithMoves(board).board;
}

export function applyColumnGravityWithMoves(board: Board): { board: Board; moves: CellGravityMove[] } {
  const next = createEmptyBoard();
  const moves: CellGravityMove[] = [];
  for (let x = 0; x < BOARD_WIDTH; x += 1) {
    let writeY = BOARD_HEIGHT - 1;
    for (let y = BOARD_HEIGHT - 1; y >= 0; y -= 1) {
      const value = board[y][x];
      if (value !== 0) {
        next[writeY][x] = value;
        if (writeY !== y) {
          moves.push({
            type: value,
            fromX: x,
            fromY: y,
            toX: x,
            toY: writeY
          });
        }
        writeY -= 1;
      }
    }
  }
  return { board: next, moves };
}

export function boardFromRows(rows: number[][]): Board {
  if (rows.length !== BOARD_HEIGHT || rows.some((row) => row.length !== BOARD_WIDTH)) {
    throw new Error(`Board must be ${BOARD_WIDTH}x${BOARD_HEIGHT}`);
  }
  return rows.map((row) => row.map((cell) => Math.max(0, Math.min(6, Math.floor(cell))) as Cell));
}

export function resolveBoard(board: Board, level: number, levelProgress: number): ResolveResult {
  let current = cloneBoard(board);
  let currentLevel = level;
  let currentProgress = levelProgress;
  let chain = 0;
  let scoreAdd = 0;
  let erased = 0;
  const waves: ResolveWave[] = [];

  while (true) {
    const matches = findMatches(current, activeTypeCount(currentLevel));
    if (matches.count === 0) {
      break;
    }

    chain += 1;
    const waveScore = scoreForWave(currentLevel, matches.count, chain);
    const levelBefore = currentLevel;
    const levelResult = applyErasedToLevel(currentLevel, currentProgress, matches.count);
    currentLevel = levelResult.level;
    currentProgress = levelResult.progress;
    scoreAdd += waveScore;
    erased += matches.count;

    waves.push({
      erased: matches.count,
      chain,
      scoreAdd: waveScore,
      levelBefore,
      levelAfter: currentLevel,
      levelUps: levelResult.levelUps
    });

    current = applyColumnGravity(eraseMarked(current, matches.cells));
  }

  return {
    board: current,
    waves,
    scoreAdd,
    level: currentLevel,
    levelProgress: currentProgress,
    erased
  };
}
